import React, { useEffect, useMemo } from 'react';

import MaskNode from 'components/shared/Nodes/MaskNode';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { buildMaskNodeStyles } from 'utils/node_util';

import { selectZoomedPPI } from 'selectors/ppi';

import ui from 'domains/ui';
import products from 'domains/products';
import svgs from 'domains/svgs';
import canvases from 'domains/canvases';

import { useCalculatedBounds } from './hooks';

import type { NodeProps } from './Node';

function MaskNodeContainer(props: NodeProps) {
  const { canvasId, node, canvasDimensions, canvasRotation, setMaskImageLoaded } = props;

  const dispatch = useAppDispatch();
  const ppi = useAppSelector(selectZoomedPPI);
  const svgData = useAppSelector((state) => svgs.selectors.selectById(state, node.svg_id || ''));
  const theme = useAppSelector(ui.selectors.theme);

  const canvasConfig = useAppSelector((state) => canvases.selectors.selectById(state, canvasId));
  const canvasProduct = useAppSelector((state) =>
    products.selectors.selectById(state, canvasConfig?.product || '')
  );

  const [x, y, w, h] = useCalculatedBounds(canvasId, node.uid);

  const borderColor = useMemo(() => canvasConfig?.borderColor, [canvasConfig]);

  const nodeDimension = useMemo(
    () => ({
      height: canvasRotation.rotation === 0 ? h : w,
      width: canvasRotation.rotation === 0 ? w : h,
    }),
    [w, h, canvasRotation.rotation]
  );

  const nodePoint = useMemo(
    () => ({
      x: canvasRotation.rotation === 0 ? x : y,
      y: canvasRotation.rotation === 0 ? y : x,
    }),
    [x, y, canvasRotation.rotation]
  );

  const shouldCenterMaskNode = useMemo(() => {
    if (!canvasProduct || !canvasConfig) return false;

    return products.utils.canRotateProduct(canvasProduct) && canvasConfig.rotation === 90;
  }, [canvasProduct, canvasConfig]);

  const styles = useMemo(() => {
    let rotation = 0;
    let dimension = { ...nodeDimension };
    let point = { ...nodePoint };

    // If we need to center the mask node, we know the rotation is 90
    // We also know the bounds have been reversed, so we need to restore the bounds
    //  to the natural orientation in order to use css transform to rotate
    if (shouldCenterMaskNode) {
      rotation = 90;
      dimension.width = nodeDimension.height;
      dimension.height = nodeDimension.width;
      point.x = (canvasDimensions.width - dimension.width) / 2;
      point.y = (canvasDimensions.height - dimension.height) / 2;
    }

    return buildMaskNodeStyles(node, dimension, point, rotation);
  }, [nodeDimension, nodePoint, node, canvasConfig, shouldCenterMaskNode, canvasDimensions]);

  // Remove border color if this mask node is removed
  useEffect(() => {
    return function cleanup() {
      dispatch(canvases.actions.updateCanvas({ borderColor: undefined }));
    };
  }, []);

  // manipulate the svg if needed
  const renderedSVG = useMemo(() => {
    if (!node.svg_id || !svgData || !svgData.data) {
      return null;
    }

    const svgContainer = svgs.utils.toParseableSVG(svgData.data);
    const withStrokes = svgContainer.querySelectorAll<SVGElement>('[data-stroke]');

    if (withStrokes.length > 0) {
      withStrokes.forEach((elem) => {
        const stroke = elem.dataset.stroke as string; // safe because this is literally what the selector was
        const strokeWidth = parseFloat(stroke) * ppi;
        elem.setAttribute('stroke-width', strokeWidth.toString());
      });
    }

    if (borderColor) {
      svgContainer.querySelectorAll('.border').forEach((elem) => {
        elem.setAttribute('style', `fill: #${borderColor}`);
      });
    }

    const textElems = svgContainer.querySelectorAll<SVGTextElement>('svg text');
    textElems.forEach((elem) => {
      // eslint-disable-next-line no-param-reassign
      elem.style.fill = '';
    });

    return svgContainer.innerHTML;
  }, [svgData, ppi, borderColor, node, theme]);

  function handleImageLoaded() {
    setMaskImageLoaded({ src: node.node_image, loaded: true });
  }

  return (
    <MaskNode node={node} styles={styles} renderedSVG={renderedSVG} onLoad={handleImageLoaded} />
  );
}

export default MaskNodeContainer;
